import usePanelState from '@databases/panel/usePanelState';
import React, { useCallback, useEffect, useRef } from 'react';
import { type ImperativePanelGroupHandle, PanelGroup } from 'react-resizable-panels';

import { storage } from './panel/storage.ts';

const PersistedPanelGroup: React.FC<
  React.PropsWithChildren<{
    storageKey: string;
    outerContainerRef?: HTMLElement | null;
  }>
> = ({ children, storageKey, outerContainerRef }) => {
  const ref = useRef<ImperativePanelGroupHandle>(null);
  const { isPanelVisible, getData } = usePanelState();
  const fieldSettingsPanelData = getData('FieldSettingsPanel');

  const onLayoutChange = useCallback(
    (sizes: number[]) => {
      storage.setItem(storageKey, sizes);
    },
    [storageKey]
  );

  const scrollToField = useCallback(() => {
    if (fieldSettingsPanelData && isPanelVisible('FieldSettingsPanel')) {
      const fieldId = fieldSettingsPanelData.fieldId;
      const field = document.getElementById(fieldId);

      if (field) {
        const containerRect = outerContainerRef?.getBoundingClientRect();
        const fieldRect = field.getBoundingClientRect();

        if (
          containerRect &&
          (fieldRect.right > containerRect.right || fieldRect.left < containerRect.left)
        ) {
          // Add scroll padding to see all four borders of cell when field settings is open
          field.style.scrollMarginLeft = '85px';
          field.style.scrollMarginRight = '10px';
          field.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }
    }
  }, [fieldSettingsPanelData, isPanelVisible, outerContainerRef]);

  useEffect(() => {
    const storageLayout = storage.getItem(storageKey);
    if (storageLayout) {
      ref.current?.setLayout(storageLayout);
    }
  }, [storageKey]);

  useEffect(() => {
    if (storageKey.includes('FieldSettingsPanel')) {
      const timeout = setTimeout(() => {
        scrollToField();
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [storageKey, scrollToField]);

  return (
    <PanelGroup
      onLayout={onLayoutChange}
      ref={ref}
      id="panel-group"
      direction="horizontal"
      style={{
        flexGrow: 1
      }}
    >
      {children}
    </PanelGroup>
  );
};

export default PersistedPanelGroup;
